import React, { useEffect } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { getSessionReportLessons } from '../../features/sessionReport/api';
import { sessionReportKeys } from '../../features/sessionReport/keys';
import { formatKst } from '../../features/sessionReport/labels';
import { SessionReportMembership } from '../../features/sessionReport/types';

export const BEFORE_ISSUE_FLAGS = new Set(['LESSON_BEFORE_START', 'SCHEDULE_BEFORE_ISSUE']);
const timestamp = (value?: string | null) => value ? Date.parse(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00+09:00` : value) : NaN;
export const isBeforeMembershipIssue = (lessonAt: string, issueAt?: string | null) => timestamp(lessonAt) < timestamp(issueAt);
const dateLabel = (value?: string | null) => Number.isFinite(timestamp(value)) ? formatKst(new Date(timestamp(value)).toISOString(), 'M월 D일') : '';

export const BeforeIssueGuidance = ({ centerId = '', membership, enabled = false }: {
    centerId?: string; membership?: SessionReportMembership; enabled?: boolean;
}) => {
    const canLoad = enabled && !!centerId && !!membership?.membershipId && Number.isFinite(timestamp(membership.startAt));
    // 수업 목록과 같은 캐시를 써요. 참고 안내를 열었을 때만 날짜를 확인해요.
    const query = useInfiniteQuery({
        queryKey: sessionReportKeys.lessons(centerId, membership?.membershipId ?? ''),
        queryFn: ({ pageParam }: { pageParam?: string }) => getSessionReportLessons({
            centerId, membershipId: membership!.membershipId, from: '', to: '', cursor: pageParam ?? null,
        }),
        getNextPageParam: (last) => last.status === 'ok' ? last.data.nextCursor ?? undefined : undefined,
        enabled: canLoad,
        retry: false, refetchOnWindowFocus: false,
    });
    const earlierLessons = query.data?.pages.flatMap((page) => page.status === 'ok' ? page.data.lessons : [])
        .filter((lesson) => isBeforeMembershipIssue(lesson.startTime, membership?.startAt)) ?? [];
    // 최신 수업부터 내려오는 여러 쪽에서도 발급 전 수업이 있는 쪽까지 확인해요.
    useEffect(() => {
        if (canLoad && !earlierLessons.length && query.hasNextPage && !query.isFetching && !query.isError) void query.fetchNextPage();
    }, [canLoad, earlierLessons.length, query.hasNextPage, query.isFetching, query.isError, query.fetchNextPage]);
    const dates = Array.from(new Set(earlierLessons.map((lesson) => dateLabel(lesson.startTime)))).filter(Boolean);
    const issueDate = dateLabel(membership?.startAt);
    return <p>{issueDate ? `${issueDate}에 발급한 수강권에서` : '수강권에서'} 그 전에 한 수업{dates.length > 0 ? `(${dates.join(', ')})` : ''}이 빠졌어요. 먼저 수업하고 나중에 결제했다면 그대로 두셔도 돼요</p>;
};
